import { MIN_SUPPORT_MESSAGE_LENGTH } from "@iec-id/shared/contracts/support";
import { type FormEvent, type ReactNode, useState } from "react";
import { cn } from "../lib/cn";
import { Button } from "./button";
import { Checkbox } from "./checkbox";
import { FilePicker, type FilePickerProps, type PickedFile } from "./file-picker";
import { Input } from "./input";
import { Select } from "./select";
import { Textarea } from "./textarea";

export interface SupportFormValues {
	readonly topic: string;
	readonly accountEmail: string;
	readonly contactEmail: string;
	readonly message: string;
}

export interface SupportFormLabels {
	readonly topic: string;
	readonly topicPlaceholder: string;
	readonly accountEmail: string;
	readonly accountEmailHint: string;
	readonly contactEmail: string;
	readonly message: string;
	readonly messagePlaceholder: string;
	readonly submit: string;
	/** Ошибки полей — приходят готовыми строками, а не собираются в ките. */
	readonly errorTopic: string;
	readonly errorContactEmail: string;
	readonly errorMessage: string;
}

export interface SupportFormProps {
	topics: readonly { readonly value: string; readonly label: string }[];
	labels: SupportFormLabels;
	/**
	 * Согласие на обработку персональных данных. Подпись — `ReactNode`, потому
	 * что внутри неё живёт ссылка, открывающая сам документ.
	 *
	 * Галочка СНЯТА по умолчанию и держит кнопку отправки заблокированной:
	 * предотмеченный чекбокс согласием по GDPR не является (ст. 4(11)).
	 */
	consentLabel: ReactNode;
	/** Загрузка вложений. `null` — форма без файлов. */
	files: Pick<
		FilePickerProps,
		| "files"
		| "onSelect"
		| "onRemove"
		| "accept"
		| "label"
		| "hint"
		| "removeLabel"
		| "disabled"
		| "disabledHint"
	> | null;
	onSubmit: (values: SupportFormValues) => void;
	pending?: boolean;
	/** Ошибка отправки целиком: 429, отказ сети, отсутствующий документ. */
	error?: string | null;
	defaults?: Partial<SupportFormValues>;
	/** Скрытое поле-ловушка: живой человек его не видит, бот заполняет всё. */
	honeypot: {
		readonly name: string;
		readonly value: string;
		readonly onChange: (value: string) => void;
	};
	className?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Форма обращения в поддержку.
 *
 * Живёт в ките, а не в приложении, потому что её показывают ДВЕ поверхности:
 * экраны входа (`sso-web`) и «Справка» витрины (`id-web`). Копия разошлась бы
 * на первой же правке — и разошлась бы молча, потому что вторую половину
 * никто не открывает.
 *
 * Ни одной строки внутри: все подписи и тексты ошибок приходят пропами.
 */
export function SupportForm({
	topics,
	labels,
	consentLabel,
	files,
	onSubmit,
	pending = false,
	error = null,
	defaults,
	honeypot,
	className,
}: SupportFormProps) {
	const [topic, setTopic] = useState(defaults?.topic ?? "");
	const [accountEmail, setAccountEmail] = useState(defaults?.accountEmail ?? "");
	const [contactEmail, setContactEmail] = useState(defaults?.contactEmail ?? "");
	const [message, setMessage] = useState(defaults?.message ?? "");
	const [consent, setConsent] = useState(false);
	const [touched, setTouched] = useState(false);

	const topicError = touched && !topic ? labels.errorTopic : null;
	const contactError =
		touched && !EMAIL_RE.test(contactEmail.trim()) ? labels.errorContactEmail : null;
	const messageError =
		touched && message.trim().length < MIN_SUPPORT_MESSAGE_LENGTH ? labels.errorMessage : null;

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		event.stopPropagation();
		setTouched(true);

		if (
			!topic ||
			!EMAIL_RE.test(contactEmail.trim()) ||
			message.trim().length < MIN_SUPPORT_MESSAGE_LENGTH
		) {
			return;
		}

		onSubmit({
			topic,
			accountEmail: accountEmail.trim(),
			contactEmail: contactEmail.trim(),
			message: message.trim(),
		});
	};

	return (
		<form noValidate onSubmit={handleSubmit} className={cn("flex flex-col gap-5", className)}>
			<div className="flex flex-col gap-3">
				<Select
					options={topics}
					value={topic}
					onValueChange={setTopic}
					label={labels.topic}
					placeholder={labels.topicPlaceholder}
					hasError={Boolean(topicError)}
				/>
				{topicError && <p className="t-meta text-danger">{topicError}</p>}

				<Input
					label={labels.accountEmail}
					value={accountEmail}
					onChange={(event) => setAccountEmail(event.target.value)}
					inputMode="email"
					autoComplete="email"
					hint={labels.accountEmailHint}
				/>

				<Input
					label={labels.contactEmail}
					value={contactEmail}
					onChange={(event) => setContactEmail(event.target.value)}
					inputMode="email"
					autoComplete="email"
					required
					{...(contactError ? { errorText: contactError } : {})}
				/>

				<Textarea
					rows={5}
					value={message}
					onChange={(event) => setMessage(event.target.value)}
					placeholder={labels.messagePlaceholder}
					aria-label={labels.message}
					hasError={Boolean(messageError)}
				/>
				{messageError && <p className="t-meta text-danger">{messageError}</p>}

				{/*
				 * Ловушка. `tabindex=-1` и `aria-hidden` уводят её и от
				 * клавиатуры, и от скринридера: заполнить её может только
				 * автомат, читающий разметку.
				 */}
				<input
					type="text"
					name={honeypot.name}
					value={honeypot.value}
					onChange={(event) => honeypot.onChange(event.target.value)}
					tabIndex={-1}
					aria-hidden="true"
					autoComplete="off"
					className="hidden"
				/>
			</div>

			{files && <FilePicker {...files} />}

			<Checkbox checked={consent} onCheckedChange={setConsent} label={consentLabel} required />

			{error && (
				<p role="alert" className="t-meta text-danger">
					{error}
				</p>
			)}

			<Button
				type="submit"
				// Пока галочка не отмечена, отправлять нечего: согласие —
				// основание обработки, а не формальность интерфейса.
				disabled={!consent || pending}
				state={pending ? "loading" : undefined}
			>
				{labels.submit}
			</Button>
		</form>
	);
}

export type { PickedFile };
